/**
 * Utility functions for detecting tenant domains in multi-tenant architecture
 */

/**
 * Checks if the current hostname is a tenant subdomain or the public domain
 * @returns true if on a tenant subdomain (e.g., anandlab.localhost), false if on public domain (localhost)
 */
export const isTenantDomain = (): boolean => {
  const hostname = window.location.hostname;

  // In development, tenant domains are subdomains like: anandlab.localhost
  // Public domain is just: localhost
  // In production, tenant domains are: anandlab.yourdomain.com
  // Public domain is: yourdomain.com or admin.yourdomain.com

  // Development check
  if (hostname.includes('localhost')) {
    // If there's a subdomain before .localhost, it's a tenant
    // e.g., "anandlab.localhost" → true, "localhost" → false
    const parts = hostname.split('.');
    return parts.length > 1 && parts[parts.length - 1] === 'localhost';
  }

  // Production check
  // If hostname has more than 2 parts and it's not 'admin' or 'www' subdomain, it's a tenant
  // e.g., "anandlab.yourdomain.com" → true, "yourdomain.com" → false, "admin.yourdomain.com" → false, "www.yourdomain.com" → false
  const parts = hostname.split('.');
  if (parts.length > 2) {
    const subdomain = parts[0];
    return subdomain !== 'admin' && subdomain !== 'www';
  }

  return false;
};

/**
 * Gets the tenant identifier from the current hostname
 * @returns tenant identifier (e.g., 'anandlab') or null if on public domain
 */
export const getTenantIdentifier = (): string | null => {
  if (!isTenantDomain()) {
    return null;
  }

  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  return parts[0]; // First part is the tenant identifier
};

/**
 * Gets the public domain base URL (without subdomain)
 * @returns base URL like 'http://localhost:5173' or 'https://yourdomain.com'
 */
export const getPublicDomainUrl = (): string => {
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  const port = window.location.port;

  // In development, return localhost without subdomain
  if (hostname.includes('localhost')) {
    return `${protocol}//localhost${port ? `:${port}` : ''}`;
  }

  // In production, return base domain (remove subdomain)
  const parts = hostname.split('.');
  const baseDomain = parts.slice(-2).join('.'); // Get last 2 parts (yourdomain.com)
  return `${protocol}//${baseDomain}`;
};
